/**
 * Format a number as currency
 */
export const formatCurrency = (amount: number | null | undefined, currency: string = 'USD'): string => {
  if (amount === 0 || amount === null || amount === undefined) return '—';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format a number in compact notation (e.g., 1.2K, 1.5M)
 */
export const formatCompactNumber = (num: number | null | undefined): string => {
  if (num === 0 || num === null || num === undefined) return '—';
  
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num);
};

/**
 * Format a number with commas
 */
export const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined || isNaN(num)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US').format(num);
};

/**
 * Format a percentage
 */
export const formatPercentage = (value: number | null | undefined, decimals: number = 2): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.00%';
  }
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format a currency amount in compact notation (e.g., $1.2K, $1.5M)
 */
export const formatCompactCurrency = (amount: number | null | undefined, currency: string = 'USD'): string => {
  if (amount === 0 || amount === null || amount === undefined) return '—';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount);
};

/**
 * Format money for charts (like in working project)
 */
export const formatMoney = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '$0';
  }
  
  const absValue = Math.abs(value);
  
  if (absValue >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`;
  } else if (absValue >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  } else if (absValue >= 1) {
    return `$${value.toLocaleString('en-US')}`;
  }
  
  return `$${value.toFixed(0)}`;
};

/**
 * Parse a number string that can be in various formats:
 * - 100000 or 100,000 (plain numbers)
 * - 100k or 100K (thousands)
 * - 1m or 1M (millions)
 * - 1b or 1B (billions)
 * Returns the number as an integer, or NaN if invalid
 */
export const parseNumberInput = (input: string): number => {
  if (!input || input.trim() === '') {
    return NaN;
  }

  // Remove commas and whitespace
  const cleaned = input.trim().replace(/,/g, '').toLowerCase();
  
  // Check if it ends with k, m, or b
  const lastChar = cleaned[cleaned.length - 1];
  
  if (lastChar === 'k') {
    const num = parseFloat(cleaned.slice(0, -1));
    return isNaN(num) ? NaN : Math.round(num * 1000);
  } else if (lastChar === 'm') {
    const num = parseFloat(cleaned.slice(0, -1));
    return isNaN(num) ? NaN : Math.round(num * 1000000);
  } else if (lastChar === 'b') {
    const num = parseFloat(cleaned.slice(0, -1));
    return isNaN(num) ? NaN : Math.round(num * 1000000000);
  } else {
    // Regular number (with or without decimals)
    const num = parseFloat(cleaned);
    return isNaN(num) ? NaN : Math.round(num);
  }
}; 